import { Box, FlexBox, Tag, Text } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { skipToken } from '@reduxjs/toolkit/query/react';
import type { ToolCallAutomationExecutionData } from 'lib-shared-legacy-types';
import { AutomationActivityType } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { conversationsApi } from 'apis/conversations';

import { getRoutedNode } from '../routedThroughAutomation';

type AutomationTaskExecutionActivityProps = {
  readonly csid: number;
  readonly executionData: ToolCallAutomationExecutionData;
};

export const AutomationTaskExecutionActivity = (
  props: AutomationTaskExecutionActivityProps,
): JSX.Element | null => {
  const {
    csid,
    executionData: { execution_id: executionId },
  } = props;

  const {
    data: {
      data: { automation_activities: automationActivities = [] } = {},
    } = {},
  } = conversationsApi.useReadConversationAutomationActivitiesQuery(
    executionId
      ? {
          csid,
          automationRunId: executionId,
        }
      : skipToken,
  );

  const integrationNode = automationActivities.find(
    (node) =>
      node.activity_type ===
      AutomationActivityType.AUTOMATION_EXTERNAL_INTEGRATION_NODE_VISITED,
  );
  const outcome = integrationNode?.outcome;
  const outcomeWasSuccess = outcome?.type === 'success';

  const outcomeText = useMemo(() => {
    if (!outcome) {
      return 'Status unknown';
    }

    if (outcomeWasSuccess) {
      return 'Ran successfully';
    }

    if (outcome?.type === 'timeout') {
      return 'Failed to run (timeout)';
    }

    return 'Failed to run';
  }, [outcome, outcomeWasSuccess]);

  const outcomeColor = useMemo(() => {
    if (!outcome) {
      return colors.neutral[400];
    }

    if (outcomeWasSuccess) {
      return colors.green[400];
    }

    return colors.red[400];
  }, [outcome, outcomeWasSuccess]);

  if (!executionId) {
    return null;
  }

  return (
    <FlexBox gap="100" flexDirection="column">
      <Text.Paragraph variant="3"> Execution activity </Text.Paragraph>
      <FlexBox flexDirection="column" padding="0 200">
        {outcome && (
          <Tag color={outcomeColor} noBorder>
            {outcomeText}
          </Tag>
        )}
        <Box padding="200 200">
          {automationActivities.map((node, index) => (
            <Text.Paragraph key={index} variant="3" subdued>
              {getRoutedNode(node)}
            </Text.Paragraph>
          ))}
        </Box>
      </FlexBox>
    </FlexBox>
  );
};
